<template>
  <div>
    <PatternlibSkeletonloader
      v-if="isLoadingHotspot"
      id="modal"
      width="250"
      style="max-height: 250px !important"
      start
    ></PatternlibSkeletonloader>
    <div
      v-show="isLoaded"
      id="modal"
      ref="modal"
      style="
        background-color: #fff;
        display: flex;
        flex-direction: column;
        z-index: 1;
        min-width: min(450px, 90%);
      "
    >
      <div style="align-self: flex-end">
        <patternlib-link @click="hide()" size="L" icon="x" />
      </div>
      <patternlib-headline
        id="name"
        heading-tag="h6"
        :headline="hotspot?.name || ''"
      >
      </patternlib-headline>
      <br />
      <div id="articleNumber">
        {{ t('positionModal.articleNumber') }}: {{ hotspot?.articleNumber }}
      </div>
      <div id="price" style="font-weight: bold" :class="$gStyle('my-1.5')">
        {{ formatCurrency(hotspot?.priceGross, hotspot?.currency) }}
      </div>

      <div
        v-if="hotspot?.stockText?.toString() !== 'NotInStock'"
        id="stock"
        :style="{ color: getColor(hotspot?.stockType) }"
      >
        <span v-if="hotspot?.stockType === 'AVAILABLE'">{{
          hotspot?.stockText
        }}</span>
        <patternlib-tooltip v-else tooltip-position="center">
          <div
            slot="content"
            style="text-align: left; color: black; padding: 10px; width: 400px"
          >
            {{ t('spareparts.outOfStock') }}
          </div>
          <div style="text-decoration: underline">
            {{ hotspot?.stockText }}
          </div>
        </patternlib-tooltip>
      </div>
      <div v-else>
        <patternlib-tooltip tooltip-position="center">
          <div
            slot="content"
            style="text-align: left; color: black; padding: 10px; width: 400px"
          >
            {{ $t('outOfStockInfo') }}
          </div>
          <div>
            {{ hotspot.stockText }}
          </div>
        </patternlib-tooltip>
      </div>
      <div :class="$gStyle('mt-2')" style="display: flex">
        <div :class="$gStyle('m-1')" style="flex-basis: 100%">
          <patternlib-textinput
            id="quantity"
            :controlled="true"
            type="number"
            :step="1"
            min="1"
            max="999"
            :value="quantity"
            @lhChange="(event) => (quantity = event.detail.value as number)"
          ></patternlib-textinput>
        </div>
        <patternlib-button
          @click="addOrderPosition()"
          :disabled="!isMultiCompontsLoaded"
          :loading="!isMultiCompontsLoaded"
          :label="$t('positionModal.addToCart')"
          width="100%"
          :class="$gStyle('m-1')"
          size="S"
        />
      </div>
      <div
        v-if="
          hotspot?.position &&
          getZPartsByPosition(hotspot?.position).value.length > 0
        "
        style="color: dimgray"
        :class="$gStyle('mt-2')"
      >
        <span>{{ $t('spareparts.zPartInfoText') }}</span
        ><br />
        <span style="font-weight: bold">{{
          $t('spareparts.zPartAutoAdd')
        }}</span>
        <div>
          <div v-for="zPart in getZPartsByPosition(hotspot?.position).value">
            <div style="font-weight: bold">
              {{ zPart.name }}
            </div>
            <div style="display: flex">
              <div style="flex-grow: 1">
                {{ $t('positionModal.articleNumber') }}:
                {{ zPart.articleNumber }}
              </div>
              <div style="text-align: end; font-weight: 900; font-weight: bold">
                {{ formatCurrency(zPart.priceGross, zPart.currency) }}
              </div>
            </div>
            <div style="display: flex">
              <patternlib-textinput
                :readonly="true"
                type="number"
                :value="quantity * (zPart.quantity || 1)"
              >
              </patternlib-textinput>
              <div style="flex-basis: 100%"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { PatternlibSkeletonloader } from '@liebherr/patternlib-vue';
  import { storeToRefs } from 'pinia';
  import { computed, inject, nextTick, ref, useTemplateRef } from 'vue';
  import type { VwSparePartShort } from '../../api/hau-itde-spareparts-purchase-shop-portal-api';
  import { formatCurrency } from '../../helper/currency';
  import { useDataStore } from '../../stores/data';
  import {
    useOrderStore,
    type ExtendedOrderPosition
  } from '../../stores/order';

  const createToast: any = inject('createToast');

  const dataStore = useDataStore();
  const { isLoadingHotspot, isMultiCompontsLoaded } = storeToRefs(dataStore);
  const { getHotSpotByPosition, getZPartsByPosition, getWebsiteConfigPage } =
    dataStore;

  const orderStore = useOrderStore();
  const { addArticle } = orderStore;

  const hotspot = ref<VwSparePartShort>();
  const quantity = ref(1);
  const isVisible = ref(false);
  const isLoaded = computed(() => isVisible && hotspot);
  const modal = useTemplateRef('modal');

  import { useI18n } from 'vue-i18n';
  import { getColor } from '../../helper/color';
  const { t } = useI18n();

  const show = async (position: number) => {
    isVisible.value = true;
    hotspot.value = getHotSpotByPosition(position).value;

    //await next updated hook => else scrollIntoView may not work, because modal isn't rendered by setting boolean isVisible above
    await nextTick();

    modal.value?.scrollIntoView({
      behavior: 'smooth'
    } as ScrollIntoViewOptions);
  };

  const hide = () => {
    isVisible.value = false;
    quantity.value = 1;
    hotspot.value = undefined;
  };

  const addOrderPosition = () => {
    const selectedHotspot = hotspot.value;
    const selectedQuantity = quantity.value;

    if (!selectedHotspot) {
      return;
    }

    addArticle(
      <ExtendedOrderPosition>{
        articleName: selectedHotspot.name,
        articleNumber: selectedHotspot.articleNumber,
        qty: selectedQuantity,
        price: selectedHotspot.priceGross || 0,
        currency: selectedHotspot.currency,
        stockText: selectedHotspot.stockText,
        stockType: selectedHotspot.stockType
      },
      selectedHotspot.position
    ).then(() => {
      createToast(
        `${t('spareparts.cartMessage', { name: selectedHotspot.name })} <u><a href='${getWebsiteConfigPage('HauShopCartUrl').value}' style='margin-left: 10px;'>${t('spareparts.cart')}</a></u>`,
        'success'
      );
    });

    hide();
  };

  defineExpose({ show, hide, isLoaded });
</script>

<style scoped>
  #modal {
    position: absolute;
    padding: 10px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    max-height: calc(100% - 2px);
    min-width: 450px;
    overflow-y: auto;
    display: flex;
  }
</style>
